import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import useTeacherClassScope from '../hooks/useTeacherClassScope';

export default function BroadcastGroupList() {
    const { http, user } = AuthUser();
    const scope = useTeacherClassScope(http, user);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [items, setItems] = useState([]);
    const [pickClass, setPickClass] = useState('');
    const [pickDivision, setPickDivision] = useState('');

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/broadcast_groups?company_id=${user.company_id}`);
            if (response.data.success) {
                setGroups(response.data.data || []);
            } else {
                toast.error(response.data.message || 'Failed to load groups');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error loading groups');
        } finally {
            setLoading(false);
        }
    };

    const addPair = () => {
        const cls = pickClass || scope.selectedClass;
        const div = pickDivision || scope.selectedDivision;
        if (!cls || !div) {
            toast.error('Select class and division');
            return;
        }
        const key = `${cls}|${div}`;
        if (items.some((i) => `${i.class}|${i.division}` === key)) {
            toast.error('Already added');
            return;
        }
        setItems((prev) => [...prev, { class: cls, division: div }]);
        setPickClass('');
        setPickDivision('');
    };

    const removePair = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error('Group name is required');
            return;
        }
        if (items.length === 0) {
            toast.error('Add at least one class + division');
            return;
        }

        setSaving(true);
        try {
            const response = await http.post('/broadcast_groups', {
                company_id: user.company_id,
                name: name.trim(),
                login_user: user.id,
                items,
            });
            if (response.data.success) {
                toast.success(response.data.message || 'Group created');
                setName('');
                setItems([]);
                fetchGroups();
            } else {
                toast.error(response.data.message || 'Failed to create');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error creating group');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this group?')) return;
        try {
            const response = await http.delete(
                `/broadcast_groups/${id}?company_id=${user.company_id}`
            );
            if (response.data.success) {
                toast.success('Group deleted');
                fetchGroups();
            } else {
                toast.error(response.data.message || 'Failed to delete');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error deleting group');
        }
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-4 px-4">
                <div className="row mb-4">
                    <div className="col-12">
                        <h3 className="mb-1">Broadcast Groups</h3>
                        <p className="text-muted mb-0">
                            Create named groups of class + division pairs. Use them when sending
                            Notices or Notifications.
                        </p>
                    </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header">
                        <h5 className="mb-0">Create Group</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleCreate}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Group name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g. Primary A & B + Grade 10"
                                        maxLength={150}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Class</label>
                                    <select
                                        className="form-control"
                                        value={pickClass || scope.selectedClass}
                                        onChange={(e) => {
                                            setPickClass(e.target.value);
                                            scope.onClassChange(e.target.value);
                                        }}
                                        disabled={scope.loadingScope}
                                    >
                                        <option value="">Select Class</option>
                                        {scope.classOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Division</label>
                                    <select
                                        className="form-control"
                                        value={pickDivision || scope.selectedDivision}
                                        onChange={(e) => {
                                            setPickDivision(e.target.value);
                                            scope.onDivisionChange(e.target.value);
                                        }}
                                        disabled={scope.loadingScope}
                                    >
                                        <option value="">Select</option>
                                        {scope.divisionOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-1 d-flex align-items-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary w-100"
                                        onClick={addPair}
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="col-12">
                                    {items.length === 0 ? (
                                        <p className="text-muted small mb-0">
                                            No pairs added yet. Add class + division combinations above.
                                        </p>
                                    ) : (
                                        <div className="d-flex flex-wrap gap-2">
                                            {items.map((item, index) => (
                                                <span
                                                    key={`${item.class}-${item.division}-${index}`}
                                                    className="badge bg-primary d-inline-flex align-items-center gap-2"
                                                    style={{ fontSize: 13, padding: '8px 10px' }}
                                                >
                                                    {item.class} - {item.division}
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white"
                                                        style={{ fontSize: 8 }}
                                                        onClick={() => removePair(index)}
                                                        aria-label="Remove"
                                                    />
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="col-12">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Create Group'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Saved Groups</h5>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={fetchGroups}
                            disabled={loading}
                        >
                            Refresh
                        </button>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status" />
                            </div>
                        ) : groups.length === 0 ? (
                            <p className="text-muted mb-0 text-center py-4">No groups yet.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Class / Division</th>
                                            <th style={{ width: 100 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groups.map((g, index) => (
                                            <tr key={g.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <strong>{g.name}</strong>
                                                </td>
                                                <td>
                                                    {(g.items || []).map((i) => i.label).join(', ') || '—'}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(g.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
